import React from 'react';
import { TextField, InputAdornment } from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/core';
import { designTokens } from '../../styles/designTokens';
import UAFlag from '../../assets/images/flag.js';

const useStyles = makeStyles((theme) => ({
    countryPrefix: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: `0 ${designTokens.spacing.md} 0 ${designTokens.spacing.sm}`,
        borderRight: `1px solid ${designTokens.colors.grey[300]}`,
    },
    countryPrefixDisabled: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: `0 ${designTokens.spacing.md} 0 ${designTokens.spacing.sm}`,
        borderRight: `1px solid ${designTokens.colors.grey[300]}`,
    },
    flag: {
        flexShrink: 0,
    },
    prefixText: {
        ...designTokens.typography.placeholder,
        color: designTokens.colors.text.primary,
        whiteSpace: 'nowrap',
    },
    prefixTextDisabled: {
        ...designTokens.typography.placeholder,
        color: designTokens.colors.text.disabled,
        whiteSpace: 'nowrap',
    }
}));

const CustomTextField = withStyles({
    root: {
        'label + &': {
            marginTop: designTokens.spacing.xl,
        },
        '& input': {
            ...designTokens.typography.placeholder,
            color: designTokens.colors.text.primary,
        },
        '& input + fieldset': {
            borderRadius: designTokens.borderRadius.button,
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: designTokens.colors.border,
        },
        '& input:valid:hover + fieldset': {
            borderColor: designTokens.colors.grey[300],
        },
        '& input:valid:focus + fieldset': {
            borderWidth: '1px',
            borderColor: designTokens.colors.primaryLight,
        },
        '& .Mui-error fieldset': {
            borderColor: `${designTokens.colors.red.main} !important`,
        },
        '& p.Mui-error': {
            ...designTokens.typography.error,
            marginTop: '4px',
        },
        '& .Mui-disabled fieldset': {
            borderColor: `${designTokens.colors.border} !important`,
        },
        '& .Mui-disabled': {
            backgroundColor: `${designTokens.colors.background.input} !important`,
            borderRadius: designTokens.borderRadius.button,
        },
        '& .Mui-disabled input': {
            backgroundColor: 'transparent !important',
            color: `${designTokens.colors.text.disabled} !important`,
        },
    },
})(TextField);

const PhoneNumberInput = ({
    value = '',
    onChange,
    label,
    disabled = false,
    autoFocus = false,
    error = false,
    helperText,
    placeholder = '(XX) XXX - XX - XX',
    onKeyPress,
    inputRef,
    fullWidth = true,
    ...rest
}) => {
    const classes = useStyles();

    const formatPhoneForDisplay = (phone) => {
        if (!phone) return '';

        const numbers = phone.replace(/\D/g, '');

        if (numbers.startsWith('380') && numbers.length === 12) {
            return `(${numbers.slice(3, 5)}) ${numbers.slice(5, 8)} - ${numbers.slice(8, 10)} - ${numbers.slice(10, 12)}`;
        }

        if (numbers.length === 9) {
            return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 5)} - ${numbers.slice(5, 7)} - ${numbers.slice(7, 9)}`;
        }

        return phone;
    };

    const formatPhoneOnInput = (value) => {
        const numbers = value.replace(/\D/g, '');
        const limited = numbers.slice(0, 9);

        if (limited.length === 0) return '';
        if (limited.length <= 2) return `(${limited}`;
        if (limited.length <= 5) return `(${limited.slice(0, 2)}) ${limited.slice(2)}`;
        if (limited.length <= 7) return `(${limited.slice(0, 2)}) ${limited.slice(2, 5)} - ${limited.slice(5)}`;
        return `(${limited.slice(0, 2)}) ${limited.slice(2, 5)} - ${limited.slice(5, 7)} - ${limited.slice(7, 9)}`;
    };

    const handleChange = (e) => {
        if (disabled || !onChange) return;

        const inputValue = e.target.value;
        const formatted = formatPhoneOnInput(inputValue);
        onChange(formatted);
    };

    const displayValue = disabled ? formatPhoneForDisplay(value) : value;

    return (
        <CustomTextField
            variant="outlined"
            placeholder={placeholder}
            value={displayValue}
            onChange={handleChange}
            onKeyPress={onKeyPress}
            fullWidth={fullWidth}
            disabled={disabled}
            error={error}
            helperText={helperText}
            label={label}
            autoFocus={autoFocus}
            inputRef={inputRef}
            inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                    }}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <div className={disabled ? classes.countryPrefixDisabled : classes.countryPrefix}>
                            <UAFlag className={classes.flag} />
                            <span className={disabled ? classes.prefixTextDisabled : classes.prefixText}>
                                +380
                            </span>
                        </div>
                    </InputAdornment>
                ),
            }}
            {...rest}
        />
    );
};

export const phoneUtils = {
    getCleanPhone: (phone) => {
        return phone.replace(/\D/g, '');
    },

    getFullPhone: (phone) => {
        const clean = phone.replace(/\D/g, '');
        if (clean.length === 9) {
            return `+380${clean}`;
        }
        if (clean.startsWith('380') && clean.length === 12) {
            return `+${clean}`;
        }
        return phone;
    },

    isValidPhone: (phone) => {
        const clean = phone.replace(/\D/g, '');
        return clean.length === 9;
    }
};

export default PhoneNumberInput;
